import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useCallback, useEffect, useState} from 'react';
import {useTeamMembers} from '../hooks/useTeamMembers';
import {listAdmins, removeAdminClaim, setAdminClaim} from '../services/teamMemberService';
import {
    ALL_TEAMS,
    FeedbackTeam,
    TEAM_COLORS,
    TEAM_LABELS,
    TeamMember,
    USER_GROUP_IDS,
    UserGroup,
} from '../types/feedback';
import {PencilSimple, ShieldCheck, ShieldSlash, Trash, UsersThree} from '@phosphor-icons/react';

const PRIMARY_COLOR = '#3ab3ff';

export function TeamMembersPage() {
    const t = useTraduire();
    const {members, loading, error, addMember, editMember, removeMember} = useTeamMembers();
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        team: '' as FeedbackTeam | '',
        userGroup: 'ASSIMIL' as UserGroup,
        canBeAssigned: true,
        hasSupportAccess: false,
        hasImmersion: false,
    });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set());
    const [togglingAdmin, setTogglingAdmin] = useState<string | null>(null);

    const fetchAdmins = useCallback(async () => {
        try {
            const admins = await listAdmins();
            setAdminEmails(admins);
        } catch (err) {
            console.error('Failed to fetch admins:', err);
        }
    }, []);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    const handleToggleAdmin = async (email: string) => {
        const isAdmin = adminEmails.has(email.toLowerCase());
        setTogglingAdmin(email);
        try {
            if (isAdmin) {
                await removeAdminClaim(email);
            } else {
                await setAdminClaim(email);
            }
            await fetchAdmins();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update admin status');
        } finally {
            setTogglingAdmin(null);
        }
    };

    const resetForm = () => {
        setFormData({
            email: '', name: '', team: '',
            userGroup: 'ASSIMIL', canBeAssigned: true,
            hasSupportAccess: false, hasImmersion: false,
        });
        setShowAddForm(false);
        setEditingMember(null);
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.email || !formData.name || !formData.team) return;

        setSaving(true);
        try {
            await addMember({
                email: formData.email,
                name: formData.name,
                team: formData.team as FeedbackTeam,
                userGroup: formData.userGroup,
                userGroupId: USER_GROUP_IDS[formData.userGroup],
                canBeAssigned: formData.canBeAssigned,
                hasSupportAccess: formData.hasSupportAccess,
                hasImmersion: formData.hasImmersion,
            });
            resetForm();
        } catch {
            // Error is handled by the hook
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingMember || !formData.name || !formData.team) return;

        setSaving(true);
        try {
            await editMember(editingMember.id, {
                name: formData.name,
                team: formData.team as FeedbackTeam,
                userGroup: formData.userGroup,
                userGroupId: USER_GROUP_IDS[formData.userGroup],
                canBeAssigned: formData.canBeAssigned,
                hasSupportAccess: formData.hasSupportAccess,
                hasImmersion: formData.hasImmersion,
            });
            resetForm();
        } catch {
            // Error is handled by the hook
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await removeMember(id);
            setDeleteConfirm(null);
        } catch {
            // Error is handled by the hook
        }
    };

    const startEdit = (member: TeamMember) => {
        setEditingMember(member);
        setFormData({
            email: member.email,
            name: member.name,
            team: member.team,
            // Les fiches anterieures a ces champs n'en portent aucun : on
            // retombe sur les memes valeurs que le modele cote application,
            // c'est-a-dire fermees, sauf l'affectation qui etait implicite.
            userGroup: member.userGroup ?? (member.userGroupId === 1 ? 'HOLIWAVE' : 'ASSIMIL'),
            canBeAssigned: member.canBeAssigned ?? true,
            hasSupportAccess: member.hasSupportAccess ?? false,
            hasImmersion: member.hasImmersion ?? false,
        });
        setShowAddForm(false);
    };

    // Group members by team
    const membersByTeam = members.reduce((acc, member) => {
        if (!acc[member.team]) {
            acc[member.team] = [];
        }
        acc[member.team].push(member);
        return acc;
    }, {} as Record<FeedbackTeam, TeamMember[]>);

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">{t(T.chargementMembres)}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
                    <UsersThree size={26} weight="regular" className="text-primary"/>
                    Team Members
                </h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowAddForm(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white rounded-pill hover:opacity-90 shadow-bo transition-colors"
                    style={{backgroundColor: PRIMARY_COLOR}}
                >
                    Add Member
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Add/Edit Form */}
            {(showAddForm || editingMember) && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                        {editingMember ? t(T.modifierLeMembre) : t(T.ajouterUnMembre)}
                    </h2>
                    <form onSubmit={editingMember ? handleEdit : handleAdd} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    disabled={!!editingMember}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Team
                                </label>
                                <select
                                    value={formData.team}
                                    onChange={(e) => setFormData({...formData, team: e.target.value as FeedbackTeam})}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                >
                                    <option value="">{t(T.choisirUneEquipe)}</option>
                                    {ALL_TEAMS.map((team) => (
                                        <option key={team} value={team}>
                                            {t(TEAM_LABELS[team])}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Entreprise : reprend les identifiants de l'enum
                            UserGroup du back-office, 1 Holiwave et 2 Assimil,
                            pour que les deux systemes nomment la meme chose
                            de la meme facon. */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company
                                </label>
                                <select
                                    value={formData.userGroup}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        userGroup: e.target.value as UserGroup,
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                >
                                    <option value="ASSIMIL">Assimil</option>
                                    <option value="HOLIWAVE">Holiwave</option>
                                </select>
                            </div>
                        </div>

                        {/* Droits. Chacun ouvre quelque chose de different :
                            ne pas les cocher par habitude. */}
                        <fieldset className="border border-gray-200 rounded-md p-4">
                            <legend className="px-2 text-sm font-medium text-gray-700">
                                Permissions
                            </legend>
                            <div className="space-y-3">
                                <label className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.canBeAssigned}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            canBeAssigned: e.target.checked,
                                        })}
                                        className="mt-1"
                                    />
                                    <span className="text-sm text-gray-700">
                                        Can be assigned feedback
                                        <span className="block text-xs text-gray-500">
                                            Appears in the assignee list.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.hasSupportAccess}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            hasSupportAccess: e.target.checked,
                                        })}
                                        className="mt-1"
                                    />
                                    <span className="text-sm text-gray-700">
                                        Support tools in the app
                                        <span className="block text-xs text-gray-500">
                                            Support mode and test content. Does not grant
                                            access to other users' data — that stays in the
                                            Firestore rules.
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.hasImmersion}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            hasImmersion: e.target.checked,
                                        })}
                                        className="mt-1"
                                    />
                                    <span className="text-sm text-gray-700">
                                        Pack Immersion
                                        <span className="block text-xs text-gray-500">
                                            Sees the offer without a subscription.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </fieldset>

                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={resetForm}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-pill hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-white rounded-pill hover:opacity-90 disabled:opacity-50 shadow-bo transition-colors"
                                style={{backgroundColor: PRIMARY_COLOR}}
                            >
                                {saving ? t(T.enregistrement) : editingMember ? t(T.mettreAJour) : t(T.ajouter)}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Members by Team */}
            {ALL_TEAMS.map((team) => (
                <div key={team} className="bg-white rounded-lg shadow overflow-hidden">
                    <div className={`px-6 py-3 ${TEAM_COLORS[team]}`}>
                        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{t(TEAM_LABELS[team])}</h2>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {(membersByTeam[team] || []).length === 0 ? (
                            <div className="px-6 py-4 text-gray-500 text-sm italic">
                                No members in this team
                            </div>
                        ) : (
                            (membersByTeam[team] || []).map((member) => (
                                <div key={member.id} className="px-6 py-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div>
                                            <div className="font-medium text-gray-900">{member.name}</div>
                                            <div className="text-sm text-gray-500">{member.email}</div>
                                        </div>
                                        <span
                                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                adminEmails.has(member.email.toLowerCase())
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-500'
                                            }`}
                                        >
                                            <span className={`w-2 h-2 rounded-full ${
                                                adminEmails.has(member.email.toLowerCase())
                                                    ? 'bg-green-500'
                                                    : 'bg-gray-400'
                                            }`}/>
                                            {adminEmails.has(member.email.toLowerCase()) ? 'Admin' : 'Member'}
                                        </span>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <button
                                            onClick={() => handleToggleAdmin(member.email)}
                                            disabled={togglingAdmin === member.email}
                                            className={`inline-flex items-center justify-center p-1.5 hover:opacity-60 transition-colors disabled:opacity-50 ${
                                                adminEmails.has(member.email.toLowerCase())
                                                    ? 'text-orange-500'
                                                    : 'text-green-500'
                                            }`}
                                            title={adminEmails.has(member.email.toLowerCase()) ? 'Revoke admin' : 'Make admin'}
                                        >
                                            {togglingAdmin === member.email
                                                ? <span
                                                    className="animate-spin w-5 h-5 border-2 border-current border-t-transparent rounded-full"/>
                                                : adminEmails.has(member.email.toLowerCase())
                                                    ? <ShieldSlash size={22} weight="regular"/>
                                                    : <ShieldCheck size={22} weight="regular"/>}
                                        </button>
                                        <button
                                            onClick={() => startEdit(member)}
                                            className="inline-flex items-center justify-center p-1.5 text-primary hover:opacity-60 transition-colors"
                                            title={t(T.modifier)}
                                        >
                                            <PencilSimple size={22} weight="regular"/>
                                        </button>
                                        {deleteConfirm === member.id ? (
                                            <div className="flex gap-2 items-center">
                                                <span className="text-sm text-red-600">{t(T.supprimerPoint)}</span>
                                                <button
                                                    onClick={() => handleDelete(member.id)}
                                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(null)}
                                                    className="text-sm text-gray-500 hover:text-gray-700"
                                                >
                                                    No
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirm(member.id)}
                                                className="inline-flex items-center justify-center p-1.5 text-[#f5012e] hover:opacity-60 transition-colors"
                                                title={t(T.supprimer)}
                                            >
                                                <Trash size={22} weight="regular"/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
